import { nanoid } from 'nanoid';
import { db } from './db';

/**
 * Your own CSS, in snippets you can switch on and off.
 *
 * This is the cheap eighty per cent of a plugin system: most "plugins" people
 * want from an editor are a different colour, a narrower column, a nicer font
 * for headings. CSS does all of that, and it cannot execute anything.
 *
 * Two guardrails, because a snippet is usually copied from somewhere:
 *
 *  - **Nothing is fetched.** `@import` and `url(https://…)` are removed before
 *    the CSS is applied, so a stylesheet cannot tell a stranger's server when
 *    you opened your notes, or pull a font that then does. `data:` URLs and
 *    markbird's own files stay.
 *  - **There is always a way out.** CSS can hide any button, including the one
 *    that would switch it off, so `?nocss=1` and the palette command
 *    "Turn off my CSS" exist and never depend on it.
 *
 * Snippets style the app, including the preview. Exports keep their own look on
 * purpose: a document you send someone should read the same for them as for you.
 */

const KEY = 'customCss';
/** Big enough for a real theme, small enough that a paste accident stays a mistake. */
export const MAX_SNIPPET_CHARS = 100_000;

export interface CssSnippet {
  id: string;
  name: string;
  enabled: boolean;
  css: string;
}

export interface SanitizedCss {
  css: string;
  /** What was taken out, so the UI can say so instead of silently changing the CSS. */
  removed: { rule: 'import' | 'remote-url'; text: string }[];
}

const IMPORT_RE = /@import\b[^;]*;?/gi;
const URL_RE = /url\(\s*(['"]?)([^'")]+)\1\s*\)/gi;

function isRemote(reference: string): boolean {
  const value = reference.trim();
  if (/^(data|blob):/i.test(value)) return false;
  return /^([a-z][a-z0-9+.-]*:)?\/\//i.test(value);
}

/** Remove what would reach the network; leave the rest exactly as written. */
export function sanitizeCss(css: string): SanitizedCss {
  const removed: SanitizedCss['removed'] = [];
  let out = css.slice(0, MAX_SNIPPET_CHARS).replace(IMPORT_RE, (match) => {
    removed.push({ rule: 'import', text: match.trim() });
    return '';
  });
  out = out.replace(URL_RE, (match, _quote: string, reference: string) => {
    if (!isRemote(reference)) return match;
    removed.push({ rule: 'remote-url', text: reference.trim() });
    // `none` keeps the declaration valid, so the rest of the rule still applies.
    return 'none';
  });
  return { css: out, removed };
}

export async function listSnippets(): Promise<CssSnippet[]> {
  const stored = (await db.kv.get(KEY))?.value as CssSnippet[] | undefined;
  return stored ?? [];
}

export async function saveSnippets(snippets: CssSnippet[]): Promise<void> {
  await db.kv.put({ key: KEY, value: snippets });
}

export function newSnippet(name: string): CssSnippet {
  return { id: nanoid(8), name, enabled: true, css: '' };
}

/** The CSS actually applied: every enabled snippet, sanitized, in order. */
export function combine(snippets: CssSnippet[]): string {
  return snippets
    .filter((snippet) => snippet.enabled && snippet.css.trim())
    .map((snippet) => `/* ${snippet.name.replace(/\*\//g, '')} */\n${sanitizeCss(snippet.css).css}`)
    .join('\n\n');
}

/** `?nocss=1` — the way back into an app your own CSS made unusable. */
export function customCssSuppressed(search = globalThis.location?.search ?? ''): boolean {
  return new URLSearchParams(search).get('nocss') === '1';
}

/** The one element that carries the reader's CSS; exports skip it by this id. */
export const CUSTOM_CSS_ID = 'mb-custom-css';

/** Put the combined CSS into the document (or take it out again). */
export function applyCustomCss(css: string, doc: Document = document): void {
  let style = doc.getElementById(CUSTOM_CSS_ID) as HTMLStyleElement | null;
  if (!css) {
    style?.remove();
    return;
  }
  if (!style) {
    style = doc.createElement('style');
    style.id = CUSTOM_CSS_ID;
    doc.head.appendChild(style);
  }
  // textContent, never innerHTML: a snippet is text, and stays text.
  style.textContent = css;
}

/** Read, combine and apply. Used at start-up and after every edit. */
export async function refreshCustomCss(): Promise<void> {
  if (customCssSuppressed()) return applyCustomCss('');
  applyCustomCss(combine(await listSnippets()));
}

/** Switch every snippet off — the palette's way out, which CSS cannot hide. */
export async function disableAllSnippets(): Promise<number> {
  const snippets = await listSnippets();
  const enabled = snippets.filter((snippet) => snippet.enabled).length;
  if (enabled) await saveSnippets(snippets.map((snippet) => ({ ...snippet, enabled: false })));
  await refreshCustomCss();
  return enabled;
}
